// Render-first animation gallery stage: the arcade prototype gallery scene
// (lighting, ground, grid, orbit) as an R3F component for the Animation
// Studio. Ported at parity from sstk-arcade packages/prototype/dev/main.ts.
import { OrbitControls, PerspectiveCamera } from '@react-three/drei';
import { useFrame } from '@react-three/fiber';
import { useEffect, useState, type CSSProperties } from 'react';
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';

import { HAND_WEAPON_OFFSET, type PrototypeClip } from './clips';
import { createPrototypeInstance, type AttachOffset, type Hand, type PrototypeInstance, type PrototypeSkin } from './instance';
import { loadPrototypeGltf, type PrototypeGltf } from './loader';
import { Viewport } from '../../runtime/viewport/Viewport';

export interface AnimationGalleryProp {
  /** GLB URL of the prop to attach (a weapon, torch, tool). */
  url: string;
  /** Which hand bone to parent it to. Default 'right'. */
  hand?: Hand;
  /** Attach offset in world metres/radians. Defaults to `HAND_WEAPON_OFFSET`,
   * tuned for a one-handed grip (the `Pistol_*` clip group). */
  offset?: AttachOffset;
}

export interface AnimationGalleryProps {
  /** Clip to play; loops so one-shots keep replaying in the gallery. */
  clip: PrototypeClip;
  /** Playback rate. Default 1. */
  speed?: number;
  /** Freeze the mixer without unmounting. */
  paused?: boolean;
  /** Body/accent recolour of the shared rig. */
  skin?: PrototypeSkin;
  /** A prop to attach to the rig's hand bone while this clip plays; the hand
   * animation carries it. Null/undefined for none. */
  prop?: AnimationGalleryProp | null;
  /**
   * Which rig to load. Defaults to the canonical Animated Base Character
   * (loadPrototypeGltf). Pass a different PrototypeGltf loader (for example
   * loadUal2StandardPrototypeGltf) to play the same clip vocabulary on a
   * different, selectable built-in rig instead - see ual2.ts. Callers that
   * switch loaders on an already-mounted stage should also change `key` so
   * the rig swap remounts GalleryRig's one-time load effect below.
   */
  loader?: () => Promise<PrototypeGltf>;
  /** Scene clear colour. Defaults to the gallery's near-black. */
  background?: string;
  style?: CSSProperties;
  className?: string;
  /** Called once the rig is loaded and playing. */
  onReady?: () => void;
}

const MAIN_MATERIAL = 'M_Main';
const ACCENT_MATERIAL = 'M_Joints';

function applySkin(root: THREE.Object3D, skin: PrototypeSkin): void {
  root.traverse((node) => {
    const mesh = node as THREE.Mesh;
    if (!mesh.isMesh) return;
    const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    for (const material of materials) {
      const std = material as THREE.MeshStandardMaterial;
      if (!std.color) continue;
      if (material.name === MAIN_MATERIAL && skin.bodyColor != null) std.color.set(skin.bodyColor);
      if (material.name === ACCENT_MATERIAL && skin.accentColor != null) std.color.set(skin.accentColor);
    }
  });
}

function disposeMaterialTextures(material: THREE.Material): void {
  // material.dispose() frees the material itself but does not cascade to
  // the textures it references (three.js leaves that to the caller); walk
  // every property looking for one, rather than naming each map slot, since
  // an imported GLB's material can carry any subset of map/normalMap/
  // roughnessMap/metalnessMap/emissiveMap/aoMap/etc.
  for (const value of Object.values(material)) {
    if (value instanceof THREE.Texture) value.dispose();
  }
}

/** Free a loaded prop's GPU resources - it is never part of the shared rig's
 * own geometry/material ownership, so nothing else disposes it for us. */
export function disposeProp(object: THREE.Object3D): void {
  object.traverse((node) => {
    const mesh = node as THREE.Mesh;
    if (!mesh.isMesh) return;
    mesh.geometry?.dispose();
    const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    for (const material of materials) {
      if (!material) continue;
      disposeMaterialTextures(material);
      material.dispose();
    }
  });
}

function GalleryRig({ clip, speed = 1, paused = false, skin, prop, loader = loadPrototypeGltf, onReady }: AnimationGalleryProps) {
  const [instance, setInstance] = useState<PrototypeInstance | null>(null);

  useEffect(() => {
    let cancelled = false;
    let created: PrototypeInstance | null = null;
    loader()
      .then((gltf) => {
        if (cancelled) return;
        // Always clone materials (default skin) so later recolours stay owned.
        created = createPrototypeInstance(gltf, { bodyColor: '#8c93a8', accentColor: '#2142d4' });
        created.fitHeight(1.8);
        created.root.traverse((node) => {
          if ((node as THREE.Mesh).isMesh) {
            node.castShadow = true;
            node.receiveShadow = true;
          }
        });
        setInstance(created);
        onReady?.();
      })
      .catch((err: unknown) => {
        console.error('Animation gallery rig failed to load:', err);
      });
    return () => {
      cancelled = true;
      created?.dispose();
    };
    // The rig loads once per mount; clip/skin changes are handled below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    instance?.play(clip, { loop: true, fade: 0.2, timeScale: speed });
  }, [instance, clip, speed]);

  useEffect(() => {
    if (instance && skin) applySkin(instance.root, skin);
  }, [instance, skin]);

  useEffect(() => {
    if (!instance || !prop) return;
    let cancelled = false;
    let attached: THREE.Object3D | null = null;
    new GLTFLoader().load(
      prop.url,
      (gltf) => {
        if (cancelled) return;
        const bone = instance.attachToHand(gltf.scene, prop.hand ?? 'right', prop.offset ?? HAND_WEAPON_OFFSET);
        if (bone) attached = gltf.scene;
        else disposeProp(gltf.scene);
      },
      undefined,
      (err) => console.warn('[gallery] prop failed to load:', err),
    );
    return () => {
      cancelled = true;
      if (attached) {
        attached.removeFromParent();
        disposeProp(attached);
      }
    };
  }, [instance, prop]);

  useFrame((_, dt) => {
    if (!paused) instance?.update(dt);
  });

  return instance ? <primitive object={instance.root} /> : null;
}

/**
 * The gallery scene: hemisphere + key + rim lighting, a circular ground disc,
 * a reference grid, and damped orbit controls targeting chest height.
 */
export function AnimationGalleryStage(props: AnimationGalleryProps) {
  const { background = '#11141b', style, className } = props;
  return (
    <div className={className} style={{ position: 'absolute', inset: 0, ...style }}>
      <Viewport shadows>
        <PerspectiveCamera makeDefault fov={45} near={0.1} far={100} position={[2.8, 1.7, 4.6]} />
        <color attach="background" args={[background]} />
        <hemisphereLight args={['#bcd2ff', '#202833', 1.1]} />
        <directionalLight
          color="#fff4e6"
          intensity={2.4}
          position={[3, 6, 4]}
          castShadow
          shadow-mapSize-width={1024}
          shadow-mapSize-height={1024}
        />
        <directionalLight color="#88aaff" intensity={0.8} position={[-4, 3, -3]} />
        <mesh rotation={[-Math.PI / 2, 0, 0]} receiveShadow>
          <circleGeometry args={[6, 48]} />
          <meshStandardMaterial color="#1a1f29" roughness={0.95} metalness={0} />
        </mesh>
        <gridHelper args={[12, 24, '#2a3344', '#222a36']} />
        <GalleryRig {...props} />
        <OrbitControls target={[0, 1, 0]} enableDamping />
      </Viewport>
    </div>
  );
}
